'use client';

import { useMemo, useState } from 'react';
import Image from 'next/image';
import LinkNoPrefetch from '@/components/link-noprefetch';
import { extractText } from '@/app/utils/extractText';
import { getThumbSrc } from '@/app/utils/podcastUtils';

export default function PodcastCard({ podcast, priority = false }) {
    const src = getThumbSrc(podcast.thumbnail);
    const title = podcast.title?.replace(/\.[^/.]+$/, '') || '';
    const [loaded, setLoaded] = useState(false);

    const authors = Array.isArray(podcast.author) ? podcast.author : [];
    const firstAuthor = authors[0];
    const authorName = firstAuthor
        ? `${firstAuthor.firstName ?? ''} ${firstAuthor.lastName ?? ''}`.trim()
        : '';

    const description = useMemo(() => {
        if (podcast.metaDescription) return podcast.metaDescription;
        try {
            return extractText(podcast.description);
        } catch (err) {
            return '';
        }
    }, [podcast.description, podcast.metaDescription]);

    return (
        <LinkNoPrefetch href={`/podcast/${podcast.urlTitle}`} className="group flex flex-col gap-3">
            <div className="relative aspect-video overflow-hidden rounded-2xl bg-gray-900">
                {!loaded && <div className="absolute inset-0 animate-pulse bg-white/10" />}
                {src ? (
                    <Image
                        src={src}
                        alt={podcast.thumbnailAlt || title}
                        fill
                        priority={priority}
                        onLoad={() => setLoaded(true)}
                        className={`object-cover transition-all duration-500 group-hover:scale-105 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-fuschia-700 via-fuschia-900 to-blue-700" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80 transition-opacity group-hover:opacity-100" />
                <div className="absolute inset-0 bg-black/40 opacity-0 backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100" />
                {description && (
                    <div className="pointer-events-none absolute inset-0 flex items-center p-5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <p className="line-clamp-8 text-sm leading-relaxed text-white/95">
                            {description}
                        </p>
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-1">
                <p className="line-clamp-2 text-base font-semibold leading-snug text-white/90 transition-colors group-hover:text-white">
                    {title}
                </p>
                {authorName && <p className="line-clamp-1 text-sm text-white/50">{authorName}</p>}
            </div>
        </LinkNoPrefetch>
    );
}
